/**
 * Componente: NextAppointmentsTable
 * Tabela com próximos atendimentos do médico
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, MapPin, Video, MoreVertical, FileText, User } from 'lucide-react';
import { Badge } from '@/components/common';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { AppointmentDTO } from '@/services/appointments';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface NextAppointmentsTableProps {
  appointments?: AppointmentDTO[];
  isLoading?: boolean;
  onViewAll?: () => void;
}

const statusConfig = {
  requested: {
    label: 'Solicitado',
    badge: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-200 border-blue-200 dark:border-blue-800',
  },
  RESERVADO_PENDENTE: {
    label: 'Solicitado',
    badge: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-200 border-blue-200 dark:border-blue-800',
  },
  scheduled: {
    label: 'Confirmado',
    badge: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-200 border-green-200 dark:border-green-800',
  },
  CONFIRMADO: {
    label: 'Confirmado',
    badge: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-200 border-green-200 dark:border-green-800',
  },
  reschedule_requested: {
    label: 'Reagendamento',
    badge: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-200 border-purple-200 dark:border-purple-800',
  },
  completed: {
    label: 'Concluído',
    badge: 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800',
  },
  cancelled: {
    label: 'Cancelado',
    badge: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 border-red-200 dark:border-red-800',
  },
  'no-show': {
    label: 'Faltou',
    badge: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-200 border-amber-200 dark:border-amber-800',
  },
  in_transit: {
    label: 'Em Trânsito',
    badge: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-200 border-orange-200 dark:border-orange-800',
  },
};

export function NextAppointmentsTable({
  appointments = [],
  isLoading = false,
  onViewAll,
}: NextAppointmentsTableProps) {
  const navigate = useNavigate();

  const getStatusLabel = (status: string) => {
    return statusConfig[status as keyof typeof statusConfig]?.label || status;
  };

  const getStatusBadgeClass = (status: string) => {
    return statusConfig[status as keyof typeof statusConfig]?.badge || 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800';
  };

  const formatTime = (appointment: AppointmentDTO) => {
    try {
      if (appointment.time && appointment.time !== 'undefined:undefined' && appointment.time !== '') {
        return appointment.time;
      }

      if (appointment.appointmentDate) {
        const date = parseISO(appointment.appointmentDate);
        if (date && isValid(date)) {
          return format(date, 'HH:mm');
        }
      }
      return '--:--';
    } catch (e) {
      return '--:--';
    }
  };

  const formatDate = (appointment: AppointmentDTO) => {
    try {
      let date: Date | null = null;
      if (appointment.appointmentDate) {
        date = parseISO(appointment.appointmentDate);
      } else if (appointment.date) {
        date = parseISO(appointment.date);
      }

      if (date && isValid(date)) {
        return format(date, 'dd/MM/yyyy', { locale: ptBR });
      }
      return '--/--/----';
    } catch (e) {
      return '--/--/----';
    }
  };

  // Pega apenas os próximos 5 agendamentos
  const upcomingAppointments = appointments.slice(0, 5);

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 border border-gray-200 dark:border-neutral-800">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Próximos Atendimentos
          </h3>
        </div>
        {appointments.length > 5 && (
          <button 
            onClick={onViewAll}
            className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 text-sm font-medium transition-colors"
          >
            Ver agenda completa
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Carregando atendimentos...</p>
        </div>
      ) : upcomingAppointments.length === 0 ? (
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-gray-300 dark:text-neutral-700 mx-auto mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Nenhuma consulta futura</p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">Próximas consultas aparecerão aqui</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Paciente
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Horário
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Ação
                </th>
              </tr>
            </thead>
            <tbody>
              {upcomingAppointments.map((appointment) => (
                <tr 
                  key={appointment.id}
                  className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/50 transition-colors"
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                        <span className="text-sm font-semibold text-teal-700 dark:text-teal-400">
                          {appointment.patient?.name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {appointment.patient?.name || 'Paciente'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatTime(appointment)}
                      </div>
                      <span className="text-xs ml-5">{formatDate(appointment)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1">
                      {appointment.type === 'video' ? (
                        <>
                          <Video className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">Telemedicina</span>
                        </>
                      ) : (
                        <>
                          <MapPin className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                          <span className="text-sm text-teal-600 dark:text-teal-400 font-medium">Visita Domiciliar</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <Badge 
                      className={`text-xs font-medium border ${getStatusBadgeClass(appointment.status)}`}
                    >
                      {getStatusLabel(appointment.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger className="p-1 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors cursor-pointer">
                        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem 
                          onClick={() => appointment.id ? navigate(`/chat/${appointment.id}`) : undefined}
                          disabled={!appointment.id}
                          className="cursor-pointer"
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          <span>Ver consulta</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => appointment.patient?.id ? navigate(`/pacientes/${appointment.patient.id}`) : undefined}
                          disabled={!appointment.patient?.id}
                          className="cursor-pointer"
                        >
                          <User className="mr-2 h-4 w-4" />
                          <span>Detalhes do paciente</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
